import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { Button,Callout } from '@ekhodealer/ekho-common/rev';

import type { ComponentType } from 'react';

interface FallbackProps {
	error: unknown;
	resetError: () => void;
}

function AppErrorFallback({ error, resetError }: FallbackProps) {
	const errorMessage = error instanceof Error ? error.message : String(error);
	const errorStack = error instanceof Error ? error.stack : undefined;

	return (
		<div role='alert' style={{ padding: 16 }}>
			<Callout
				variant='error'
				appearance='filled'
				title='Something went wrong'
				message={errorMessage}>
				<div style={{ marginTop: 12 }}>
					<Button onPress={resetError}>Try again</Button>
				</div>
				{process.env.NODE_ENV !== 'production' && errorStack && (
					<pre style={{ marginTop: 16, whiteSpace: 'pre-wrap' }}>{errorStack}</pre>
				)}
			</Callout>
		</div>
	);
}

export function ErrorBoundaryWrapper({
	children,
	fallbackComponent = AppErrorFallback,
}: {
	children: React.ReactNode;
	fallbackComponent?: ComponentType<FallbackProps>;
}) {
	const queryClient = useQueryClient();
	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<Sentry.ErrorBoundary
					fallback={({ error, resetError }) => {
						const FallbackComponent = fallbackComponent;
						const handleReset = () => {
							// Clear failing queries when the user retries from your fallback
							queryClient.resetQueries();
							reset();
							resetError();
						};
						return <FallbackComponent error={error} resetError={handleReset} />;
					}}>
					{children}
				</Sentry.ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
}
